<template>
  <v-card>
    <v-card-title class="primary">
      <span class="headline white--text">{{ title }}</span>
      <v-spacer/>
      <Icon
      icon="mdi-information-outline"
      iconColor="white"
      :tooltipText="$t('showDetails')"
      :small="false"
      @icon-clicked="$emit('click:details', propAccount)"/>
      <BaseDialogActions hideRefresh/>
    </v-card-title>
    <v-card-text>
      <v-row dense justify="center" align="center">
        <v-col cols="12" ref="imageContainer">
          <img v-if="qrCodeLoader.success" :src="dataUrl" :draggable="false" :style="imageStyle"/>
        </v-col>
        <v-col cols="12" class="d-flex justify-center">
          <v-btn
          outlined
          color="primary"
          :style="controlWidthStyle"
          :loading="downloading"
          :disabled="downloading"
          @click="toPdf()"
          id="account-driver-qr-code-download">
            <v-icon class="mr-1">mdi-download</v-icon>
            {{$t('downloadDriverIdSheet')}}
          </v-btn>
        </v-col>
        <v-col v-if="trailerIdentifiers.length > 0" cols="12" class="d-flex justify-center">
          <v-autocomplete
            v-model="selectedTrailers"
            :items="trailerIdentifiers"
            :item-text="trailerLabel"
            item-value="trailerIdentifierId"
            return-object
            multiple
            dense
            outlined
            hide-details
            class="flex-grow-0"
            :style="controlWidthStyle"
            :label="$t('selectTrailersToDownload')"
            :disabled="downloading">
            <template #append-outer>
              <Icon
                icon="mdi-information-outline"
                iconColor="info"
                :small="false"
                :tooltipText="$t('leaveBlankForGenericDriverIdSheet')"/>
            </template>
            <template #selection="{ item }">
              <v-chip
                small
                close
                class="my-1 mr-1"
                @click:close="chipClosed(item)"
              >
                {{ trailerLabel(item) }}
              </v-chip>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import { ResourceLoader } from '@/utils/ResourceLoader'
import { tickTock } from '@/utils/componentHelpers'
import { generateAccountDriverSheetPdf, getAccountDriverQrCodeData, buildDriverSheetPages, compareTrailerIdentifierValues } from '@/utils/pdf-formatters/AccountIdentification'
import { getLegnaQrCodeUrl } from '@/utils/LegnaQrCode'
import { VehicleType } from '@/utils/Enumerations'
import { cloneDeep } from 'lodash'

export default {
  name: 'DriverQrCode',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue')
  },

  props: {
    propAccount: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    dataUrl: undefined,
    isMounted: false,
    imageSize: 0,
    imageOffset: { x: 0, y: 0 },
    qrCodeLoader: ResourceLoader.empty,
    selectedTrailers: [],
    downloading: false
  }),

  watch: {
    vuetifyBreakpointName () {
      this.calculateImageSize()
      this.repeatInterval(this.calculateImageSize, 500, 2)
    },
    dialogFullscreen () {
      this.calculateImageSize()
      this.repeatInterval(this.calculateImageSize, 400, 2)
    }
  },

  computed: {
    ...mapGetters('dialog', ['topDialogState']),
    ...mapGetters('user', ['companyInfo']),
    title () {
      return `Account Driver ID for ${this.propAccount?.name}`
    },

    imageStyle () {
      return `
        image-rendering: pixelated;
        display: inline-block;
        margin-left: auto;
        margin-right: auto;
        transform: translate(${this.imageOffset.x}px, ${0}px);
      `
    },

    controlWidthStyle () {
      return this.imageSize > 0
        ? `width: ${this.imageSize}px; max-width: 100%;`
        : 'max-width: 100%;'
    },

    vuetifyBreakpointName () {
      return this.$vuetify?.breakpoint?.name ?? ''
    },

    dialogFullscreen () {
      return this.topDialogState?.fullscreen ?? false
    },

    trailerIdentifiers () {
      return cloneDeep(this.propAccount?.driverDetail?.trailerIdentifiers ?? []).sort(compareTrailerIdentifierValues)
    }
  },

  async created () {
    this.qrCodeLoader = new ResourceLoader(async () => {
      await this.calculateImageSize(this.$refs.imageContainer)
      this.setQrCode()
    })
    window.addEventListener(
      'resize',
      () => {
        this.onResize()
        this.repeatInterval(this.onResize, 400, 2)
      },
      { passive: true }
    )
  },

  async mounted () {
    if (this.$refs.canvasRef) {
      this.$refs.canvasRef.width = 500
      this.$refs.canvasRef.height = 500
    }
    await this.calculateImageSize(this.$refs.imageContainer)
    await this.qrCodeLoader.load()
  },

  beforeDestroy () {
    window.removeEventListener(
      'resize',
      () => {
        this.onResize()
        this.repeatInterval(this.onResize, 400, 2)
      },
      { passive: true }
    )
  },

  methods: {
    tickTock,
    generateAccountDriverSheetPdf,
    async calculateImageSize () {
      const container = this.$refs.imageContainer
      await this.tickTock(
        () => {
          this.imageSize = (container)
            ? Math.min(container.clientWidth, window.outerHeight - 230) - 200
            : 0
        },
        () => {
          this.imageOffset = {
            x: (container) ? (container.clientWidth - this.imageSize) / 2 : 0,
            y: (container) ? (container.clientHeight - this.imageSize) / 2 : 0
          }
        }
      )
    },

    repeatInterval (action, interval, n) {
      const intervalId = setInterval(() => {
        action()
        if (--n === 0) clearInterval(intervalId)
      }, interval)
    },

    async setQrCode () {
      const text = getAccountDriverQrCodeData(this.propAccount, this.companyInfo)
      this.dataUrl = await getLegnaQrCodeUrl(this.imageSize, text)
    },

    async onResize () {
      await this.calculateImageSize()
      await this.setQrCode()
    },

    trailerLabel (ti) {
      return `${ti.value} — ${VehicleType.fromInt(ti.vehicleType)}`
    },

    chipClosed (item) {
      this.selectedTrailers = this.selectedTrailers.filter(t => t.trailerIdentifierId !== item.trailerIdentifierId)
    },

    async toPdf () {
      this.downloading = true
      try {
        const pages = await buildDriverSheetPages(this.propAccount, this.companyInfo, this.selectedTrailers)
        await generateAccountDriverSheetPdf(this.propAccount, pages, true)
      } finally {
        this.downloading = false
      }
    }
  }
}
</script>
